import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown } from 'react-icons/fa';
import './TimelineNode.scss';

const TimelineNode = ({
  event,
  isExpanded,
  onClick,
  skills,
  period,
  index,
  totalCount,
  isMobile,
}) => {
  const [highlightedSkills, setHighlightedSkills] = useState([]); // set when a PROJECT is hovered — drives tag highlighting
  const [hoveredTagSkill, setHoveredTagSkill] = useState(null);   // set when a TAG is hovered — drives project highlighting
  const [expandedProjects, setExpandedProjects] = useState({});  // mobile only — per-project "show details" toggle, default closed

  const renderWithBold = (text, { plainLinks = false } = {}) => {
    const renderLinks = (part) => part.split(/(\[[^\]]+\]\(https?:\/\/[^\s)]+\))/g).map((segment, index) => {
      const link = segment.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);
      return link ? (
        <a
          key={index}
          href={link[2]}
          target="_blank"
          rel="noopener noreferrer"
          className={plainLinks ? 'nd-plain-link' : undefined}
          onClick={e => e.stopPropagation()}
        >
          {link[1]}
        </a>
      ) : segment;
    });
    const parts = text.split('**');
    return parts.map((part, i) =>
      i % 2 === 1 ? <em key={i}>{renderLinks(part)}</em> : <React.Fragment key={i}>{renderLinks(part)}</React.Fragment>
    );
  };

  const toggleProjectDetails = (idx) => {
    setExpandedProjects(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const isPhD =
    (event.type === 'work' && event.position?.includes('Ph.D')) ||
    event.major?.includes('Ph.D');

  const renderDetails = () => {
    if (isPhD) return renderResearchHighlights();
    if (event.type === 'work') return renderProjects();
    return renderCourses();
  };
    
  const renderProjects = () => {
    if (!event.projects || event.projects.length === 0) return null;
    return (
      <div className="nd-details">
        <h4 className="nd-details-title">Highlights</h4>
        <div className="nd-project-cards">
          {event.projects.map((project, idx) => {
            const matches =
              hoveredTagSkill !== null &&
              project.highlightSkills?.includes(hoveredTagSkill);
            const dimmed = hoveredTagSkill !== null && !matches;
            const isOpen = !!expandedProjects[idx];
            const cardCls = `nd-project-card${matches ? ' nd-project-card-highlighted' : ''}${dimmed ? ' nd-project-card-dimmed' : ''}${isMobile ? ' nd-project-card-clickable' : ''}`;
            const details = (
              <>
                <div className="nd-project-desc">{renderWithBold(project.description)}</div>
                {project.status === 'ongoing' && (
                  <div className="nd-project-status">Ongoing</div>
                )}
                {project.status === 'published' && (
                  <div className="nd-project-status">Published as paper</div>
                )}
              </>
            );
            return (
            <motion.div
              key={idx}
              className={cardCls}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -2, scale: 1.02 }}
              transition={{ duration: 0.3, delay: idx * 0.1 }}
              onClick={isMobile ? () => toggleProjectDetails(idx) : undefined}
            >
              <div className="nd-project-name">
                {project.publicationId ? (
                  <Link
                    to={`/publications#${project.publicationId}`}
                    className="nd-project-link"
                    onClick={e => e.stopPropagation()}
                  >
                    {project.name}
                  </Link>
                ) : (
                  project.name
                )}
              </div>
              {(!isMobile || isOpen) && details}
              {isMobile && (
                <span className="nd-project-readmore">{isOpen ? 'Show less' : 'Read more →'}</span>
              )}
            </motion.div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderResearchHighlights = () => {
    if (!event.projects || event.projects.length === 0) return null;
    return (
      <div className="nd-details">
        <h4 className="nd-details-title">Projects</h4>
        <div className="nd-project-cards">
          {event.projects.map((project, idx) => {
            const isOpen = !!expandedProjects[idx];
            const dimmed = hoveredTagSkill !== null && !project.highlightSkills?.includes(hoveredTagSkill);
            const cardCls = `nd-project-card${dimmed ? ' nd-project-card-dimmed' : ''}${isMobile ? ' nd-project-card-clickable' : ''}`;
            const details = (
              <>
                <div className="nd-project-desc">{renderWithBold(project.description)}</div>
                {project.status === 'ongoing' && (
                  <div className="nd-project-status">Ongoing</div>
                )}
                {project.status === 'published' && (
                  <div className="nd-project-status">Published as paper</div>
                )}
              </>
            );
            return (
            <motion.div
              key={idx}
              className={cardCls}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -2, scale: 1.02 }}
              transition={{ duration: 0.3, delay: idx * 0.1 }}
              onMouseEnter={() => project.highlightSkills && setHighlightedSkills(project.highlightSkills)}
              onMouseLeave={() => setHighlightedSkills([])}
              onClick={isMobile ? () => toggleProjectDetails(idx) : undefined}
            >
              <div className="nd-project-name">
                {project.publicationId ? (
                  <Link
                    to={`/publications#${project.publicationId}`}
                    className="nd-project-link"
                    onClick={e => e.stopPropagation()}
                  >
                    {project.name}
                  </Link>
                ) : (
                  project.name
                )}
                {project.link && (
                  <a href={project.link} target="_blank" rel="noopener noreferrer" className="nd-paper-link" onClick={e => e.stopPropagation()}>
                    [Paper]
                  </a>
                )}
              </div>
              {(!isMobile || isOpen) && details}
              {isMobile && (
                <span className="nd-project-readmore">{isOpen ? 'Show less' : 'Read more →'}</span>
              )}
            </motion.div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderCourses = () => {
    if (!event.courses || event.courses.length === 0) return null;
    return (
      <div className="nd-details">
        <h4 className="nd-details-title">Selected Courses</h4>
        <ul className="nd-details-list nd-courses">
          {event.courses.map((course, idx) => (
            <li key={idx}>{course.name}</li>
          ))}
        </ul>
      </div>
    );
  };

  const title = event.type === 'work' ? event.position
    : event.type === 'research-visit' ? event.role
    : event.major;
  const organization = event.type === 'work' ? event.company
    : event.type === 'research-visit' ? event.institution
    : event.university;
  const hasExpandableContent = (event.projects?.length > 0 || event.courses?.length > 0);

  const tagColorMap = {
    'LLM Alignment': 'purple',
    'LLM Evaluation': 'blue',
    'LLM Reasoning': 'green',
    'AI Theory': 'gold',
  };
  const hoveredSkill = skills.find(s => s.id === hoveredTagSkill);
  const hoveredColor = hoveredSkill ? (tagColorMap[hoveredSkill.name] ?? '') : '';

  return (
    <div className="nd-entry">
      {hasExpandableContent && (
        <button className={`nd-expand-btn${isExpanded ? ' open' : ''}`} onClick={onClick} aria-label={isExpanded ? 'Collapse' : 'Expand'}>
          <FaChevronDown />
        </button>
      )}
      <h3 className={`nd-title${hasExpandableContent ? ' nd-title-with-btn' : ''}`}>
        {event.type === 'education'
          ? <img className="nd-title-icon nd-title-icon-edu" src={`${process.env.PUBLIC_URL}/education.svg`} width={18} height={18} alt="" />
          : event.type === 'research-visit'
          ? <img className="nd-title-icon nd-title-icon-visit" src={`${process.env.PUBLIC_URL}/researh_visit.svg`} width={18} height={18} alt="" />
          : <img className="nd-title-icon nd-title-icon-work" src={`${process.env.PUBLIC_URL}/work.svg`} width={18} height={18} alt="" />}
        {title}
      </h3>
      <p className="nd-org">
        {event.link ? (
          <a href={event.link} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
            {organization}
          </a>
        ) : organization}
      </p>
      {event.type === 'research-visit' && event.host && (
        <p className="nd-advisor">
          {event.coHost ? 'Hosts' : 'Host'}:{' '}
          {event.hostLink ? (
            <a href={event.hostLink} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
              {event.host}
            </a>
          ) : event.host}
          {event.coHost && (
            <>
              {' & '}
              {event.coHostLink ? (
                <a href={event.coHostLink} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                  {event.coHost}
                </a>
              ) : event.coHost}
            </>
          )}
        </p>
      )}
      {event.type === 'research-visit' && event.note && (
        <p className="nd-visit-note">{renderWithBold(event.note, { plainLinks: true })}</p>
      )}
      {event.type === 'education' && event.advisor && (
        <p className="nd-advisor">
          {event.coAdvisor ? 'Supervisors' : 'Supervisor'}:{' '}
          {event.advisorLink ? (
            <a href={event.advisorLink} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
              {event.advisor}
            </a>
          ) : event.advisor}
          {event.coAdvisor && (
            <>
              {' & '}
              {event.coAdvisorLink ? (
                <a href={event.coAdvisorLink} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                  {event.coAdvisor}
                </a>
              ) : event.coAdvisor}
            </>
          )}
        </p>
      )}

      {isPhD && <div className="nd-tags">
        <span className="nd-tags-label">Research Interests:</span>
        {event.skillIds.map((skillId) => {
          const skill = skills.find((s) => s.id === skillId);
          if (!skill) return null;
          const linkMap = {
            'LLM Alignment': '/research#llm-alignment',
            'LLM Evaluation': '/research#llm-evaluation',
            'LLM Reasoning': '/research#llm-reasoning',
            'AI Theory': '/research#ai-theory',
          };
          const href = linkMap[skill.name];
          const isHighlighted = highlightedSkills.includes(skillId);
          const isDimmed = highlightedSkills.length > 0 && !isHighlighted;
          const cls = `nd-tag ${isHighlighted ? 'nd-tag-highlighted' : ''} ${isDimmed ? 'nd-tag-dimmed' : ''}`;
          return href ? (
            <Link
              key={skillId}
              to={href}
              state={{ from: 'resume' }}
              className={`${cls} nd-tag-link`}
              onClick={e => e.stopPropagation()}
              onMouseEnter={() => setHoveredTagSkill(skillId)}
              onMouseLeave={() => setHoveredTagSkill(null)}
            >
              {skill.name}
            </Link>
          ) : (
            <span
              key={skillId}
              className={cls}
              onMouseEnter={() => setHoveredTagSkill(skillId)}
              onMouseLeave={() => setHoveredTagSkill(null)}
            >
              {skill.name}
            </span>
          );
        })}
      </div>}

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            style={{ overflow: 'clip', overflowClipMargin: '30px' }}
            onClick={e => e.stopPropagation()}
          >
            {renderDetails()}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TimelineNode;
